import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import AxiosInstance from "../../components/Axios";
import MySelectField from "../../components/forms/MySelectField";
import MyTextField from "../../components/forms/MyTextField";
import MyAutocompleteField from "../../components/forms/MyAutocompleteField";
import MyDatePickerField from "../../components/forms/MyDatePickerField";
import dayjs from "dayjs";
import axios from "axios";
import colors from "../../components/constants/Colors";

const IPRTypeOptions = [
  "Patent",
  "Trademark",
  "Registered design",
  "Utility model",
  "Others",
];
const ApplicationReferences = ["IPFR1012", "IPTN012", "IPIT1012"];
const IPRStatus = ["Approved", "Pending", "Rejected", "N/A"];
const CreateIPR = () => {
  const [partners, setPartners] = useState([]);
  const navigate = useNavigate();
  const project_id = localStorage.getItem("project_id");

  const defaultValues = {
    type_ipr: "",
    application_title: "",
    application_reference: "",
    application_date: null,
    ipr_owner: [],
    ipr_status: "",
    award_reference: "",
  };

  const schema = yup.object({
    type_ipr: yup.string().required("Type of IPR is a required field"),
    application_title: yup
      .string()
      .required("Application title is a required field"),
    application_reference: yup
      .string()
      .required("Application reference is a required field"),
    application_date: yup
      .date()
      .required("Application date is a required field"),
    ipr_owner: yup
      .array()
      .of(
        yup.number().required() // Assuming IDs are numbers
      )
      .min(1, "At least one IPR owner is required"),
    ipr_status: yup.string().required("IPR status is a required field"),
    award_reference: yup
      .string()
      .required("Award reference is a required field"),
  });

  const { handleSubmit, control, setValue, getValues } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const submission = async (data) => {
    const formattedDate = data.application_date
      ? dayjs(data.application_date).format("YYYY-MM-DD")
      : null;

    try {
      await AxiosInstance.post(`projects/${project_id}/ipr/`, {
        type_ipr: data.type_ipr,
        application_title: data.application_title,
        application_reference: data.application_reference,
        application_date: formattedDate,
        ipr_owner: data.ipr_owner, // Assuming this is an array of IDs
        ipr_status: data.ipr_status,
        award_reference: data.award_reference,
      });
      navigate(`/monitoring`);
    } catch (error) {
      console.error("There was an error submitting the form!", error);
    }
  };

  const getPartnersInProject = async () => {
    try {
      // Make GET request to fetch partners for the given project
      const response = await axios.get(
        ` https://eutracker.rdiup.com/v1/projects/${project_id}/get_partners/`
      );

      if (response.status === 200) {
        // Successfully fetched partners
        const partners = response.data;
        setPartners(response.data);
        // You can process the partners data here, e.g., update your state or UI
        return partners; // Return the data for further use if needed
      }
    } catch (error) {
      console.error("There was an error fetching partners!", error);

      // Prepare error message
      let errorMessage = "An unexpected error occurred. Please try again.";

      if (error.response && error.response.data) {
        const errorDetails = error.response.data.details || {};
        const formattedErrors = Object.entries(errorDetails)
          .map(([field, messages]) => {
            return messages.map((msg) => `${field}: ${msg}`).join("<br>");
          })
          .join("<br>");

        errorMessage = formattedErrors || errorMessage;
      }
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await axios.get(
        " https://eutracker.rdiup.com/v1/partners/"
      );
      setPartners(
        response.data.map((partner) => ({ id: partner.id, ...partner }))
      );
    } catch (error) {
      console.error("Failed to fetch partners", error);
    }
  };

  useEffect(() => {
    if (project_id) {
      getPartnersInProject();
    }
    fetchPartners();
  }, []);

  const partnersWithAddOption = [
    ...partners,
    { id: "add-new-partner", name: "Add New Partner", pic: "" },
  ];

  return (
    <div>
      <form onSubmit={handleSubmit(submission)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            backgroundColor: colors.secondary,
            marginBottom: "10px",
            borderTopRightRadius: 5,
            borderTopLeftRadius: 5,
          }}
        >
          <Typography sx={{ marginLeft: "20px", color: "#fff" }}>
            Add an IPR
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            width: "100%",
            boxShadow: 3,
            padding: 4,
            flexDirection: "column",
            marginTop: -1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              marginBottom: "40px",
            }}
          >
            <MySelectField
              label="Type of IPR"
              name="type_ipr"
              control={control}
              width={"30%"}
              options={IPRTypeOptions}
            />
            <MyTextField
              label="Application title"
              name="application_title"
              placeholder="Provide a title"
              control={control}
              width={"30%"}
              marginTop={15}
            />
            <MyTextField
              label="Application reference"
              name="application_reference"
              placeholder="Provide a reference"
              control={control}
              width={"30%"}
              marginTop={15}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              marginBottom: "40px",
            }}
          >
            <MyDatePickerField
              label="Application Date"
              name="application_date"
              control={control}
              width={"30%"}
            />
            <FormControl sx={{ width: 250 }} fullWidth>
              {partners.length > 0 && (
                <Controller
                  name="ipr_owner"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={partners}
                      getOptionLabel={(option) =>
                        `${option.name} (${option.pic})`
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Add existing partners" />
                      )}
                      onChange={(event, value) => {
                        // Extract IDs from selected objects
                        const ids = value.map((owner) => owner.id); // Change `id` to the actual key used for primary keys
                        setValue("ipr_owner", ids);
                      }}
                      value={partners.filter((partner) =>
                        getValues("ipr_owner").includes(partner.id)
                      )} // Filter selected items based on IDs
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      } // Assuming IDs are used for comparison
                      fullWidth
                    />
                  )}
                />
              )}
            </FormControl>
            <MySelectField
              label="IPR Status"
              name="ipr_status"
              control={control}
              width={"30%"}
              options={IPRStatus}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <MyTextField
              label="APR Award Reference ID"
              name="award_reference"
              placeholder="Provide an award reference"
              control={control}
              width={"30%"}
            />
            <Box sx={{ width: "30%" }}>
              <Button
                variant="contained"
                type="submit"
                sx={{ width: "100%", backgroundColor: colors.secondary }}
              >
                Add IPR
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/")}
                sx={{ width: "100%", backgroundColor: colors.secondary, mt: 2 }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
    </div>
  );
};

export default CreateIPR;
